import React, { useEffect, useState } from 'react';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import bookingService from '../../../services/booking.service';
import NotesModal from './NotesModal';
import { showError, showSuccess } from '../../../utils/ecxSwal';
import {
    formatBookingDate,
    formatBookingDateTime,
    getBookingStatusMeta,
    getPatientInitials,
    getPatientTypeMeta,
} from './subparts/webBookingsQuery';
import { getNavIconClass } from '../../../navigation/clinicNavConfig';
import { getWebBookingAvatarTheme } from './subparts/webBookingsTheme';

function CopyableValue({ value, label }) {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) {
            return undefined;
        }
        const timer = window.setTimeout(() => setCopied(false), 2500);
        return () => window.clearTimeout(timer);
    }, [copied]);

    if (!value) {
        return <span className="text-muted">—</span>;
    }

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
        } catch (error) {
            console.error('Failed to copy value', error);
            showError({ title: 'Copy Failed', text: `Unable to copy ${label.toLowerCase()}.` });
        }
    };

    return (
        <span className="web-bookings-copy">
            <span className="text-truncate">{value}</span>
            <button
                type="button"
                className={`web-bookings-copy__btn${copied ? ' is-copied' : ''}`}
                title={copied ? 'Copied' : `Copy ${label}`}
                onClick={handleCopy}
                disabled={copied}
                aria-label={copied ? `${label} copied` : `Copy ${label}`}
            >
                <i className={`bi ${copied ? 'bi-check-lg' : 'bi-copy'}`}></i>
            </button>
        </span>
    );
}

function BookingPatientCard({ request, index = 0, handleUpdate }) {
    const [showNotes, setShowNotes] = useState(false);
    const [updating, setUpdating] = useState(false);
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const statusMeta = getBookingStatusMeta(request.status);
    const patientTypeMeta = getPatientTypeMeta(request.isNewPatient);
    const avatarTheme = getWebBookingAvatarTheme(request.id, index);

    const updateBookingStatus = async (statusId) => {
        setUpdating(true);
        try {
            const response = await bookingService.updateBookingRequests(clinicId, {
                ...request,
                status: statusId,
            });

            if (response?.status === 200) {
                showSuccess({
                    title: 'Status Updated',
                    text:
                        statusId === 2
                            ? 'Booking request marked as processed.'
                            : 'Booking request moved to follow up.',
                });
                handleUpdate();
            }
        } catch (error) {
            console.error('Error updating booking:', error);
            showError({ title: 'Update Failed', text: 'Failed to update booking request status.' });
        } finally {
            setUpdating(false);
        }
    };

    const hasPatientComments = request.comments && request.comments !== 'No Comments\n';
    const appointmentType = request.bookingAppointmentType?.apptType || '—';
    const appointmentSlot = request.bookingAppointmentType?.timeSlot
        ? `${request.bookingAppointmentType.timeSlot} min`
        : null;

    return (
        <>
            <article className="web-bookings-card">
                <header className="web-bookings-card__header">
                    <div className="web-bookings-card__identity">
                        <span
                            className={`web-bookings-avatar ecx-nav-icon ${getNavIconClass(avatarTheme)}`}
                        >
                            {getPatientInitials(request.firstName, request.lastName)}
                        </span>
                        <div className="min-width-0">
                            <div className="web-bookings-card__title-row">
                                <h3 className="web-bookings-card__name">
                                    {request.firstName} {request.lastName}
                                    {request.preferredName ? (
                                        <span className="web-bookings-card__preferred">
                                            ({request.preferredName})
                                        </span>
                                    ) : null}
                                </h3>
                                <span className={patientTypeMeta.className}>{patientTypeMeta.label}</span>
                                <span className={statusMeta.className}>{statusMeta.label}</span>
                            </div>
                            <p className="web-bookings-card__submitted mb-0">
                                Submitted {formatBookingDateTime(request.createdAt)}
                            </p>
                        </div>
                    </div>

                    <div className="web-bookings-card__actions">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setShowNotes(true)}
                            disabled={updating}
                        >
                            <i className="bi bi-journal-text me-1"></i>
                            Notes
                        </button>
                        {request.status === 0 && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => updateBookingStatus(1)}
                                disabled={updating}
                            >
                                Follow up
                            </button>
                        )}
                        {(request.status === 0 || request.status === 1) && (
                            <button
                                type="button"
                                className="btn btn-sm btn-accent"
                                onClick={() => updateBookingStatus(2)}
                                disabled={updating}
                            >
                                Mark processed
                            </button>
                        )}
                    </div>
                </header>

                <div className="web-bookings-card__body">
                    <dl className="web-bookings-meta">
                        <div className="web-bookings-meta__item">
                            <dt>Phone</dt>
                            <dd>
                                <CopyableValue value={request.cellPhone || request.homePhone} label="Phone" />
                            </dd>
                        </div>
                        <div className="web-bookings-meta__item">
                            <dt>Email</dt>
                            <dd>
                                <CopyableValue value={request.email} label="Email" />
                            </dd>
                        </div>
                        <div className="web-bookings-meta__item">
                            <dt>Appointment</dt>
                            <dd>
                                {appointmentType}
                                {appointmentSlot ? (
                                    <span className="text-muted"> · {appointmentSlot}</span>
                                ) : null}
                            </dd>
                        </div>
                        <div className="web-bookings-meta__item">
                            <dt>Doctor</dt>
                            <dd>{request.doctorName || '—'}</dd>
                        </div>
                        <div className="web-bookings-meta__item">
                            <dt>1st preference</dt>
                            <dd>
                                {formatBookingDate(request.firstPreferredDate)}
                                {request.firstPreferredTime
                                    ? ` · ${request.firstPreferredTime}`
                                    : ''}
                            </dd>
                        </div>
                        <div className="web-bookings-meta__item">
                            <dt>2nd preference</dt>
                            <dd>
                                {formatBookingDate(request.secondPreferredDate)}
                                {request.secondPreferredTime
                                    ? ` · ${request.secondPreferredTime}`
                                    : ''}
                            </dd>
                        </div>
                        <div className="web-bookings-meta__item web-bookings-meta__item--wide">
                            <dt>Reason</dt>
                            <dd>{request.reason || '—'}</dd>
                        </div>
                    </dl>

                    {(hasPatientComments || request.clinicComments) && (
                        <div className="web-bookings-card__notes">
                            {hasPatientComments ? (
                                <div className="web-bookings-note">
                                    <strong>Patient comment</strong>
                                    <p className="mb-0">{request.comments}</p>
                                </div>
                            ) : null}
                            {request.clinicComments ? (
                                <div className="web-bookings-note web-bookings-note--clinic">
                                    <strong>Clinic notes</strong>
                                    <p className="mb-0">{request.clinicComments}</p>
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            </article>

            <NotesModal
                show={showNotes}
                onClose={() => setShowNotes(false)}
                patientBooking={request}
                handleUpdate={handleUpdate}
            />
        </>
    );
}

export default BookingPatientCard;
